// ── @paramms/chat-widget/core — the headless SDK ─────────────────────────────
// Everything you need to build your OWN chat UI (an in-app messenger, a
// marketplace inbox, a full chat app) on the Relay protocol, with zero DOM or
// React dependencies. This is not a new client: it is the exact transport,
// store, outbox, and E2E machinery the bundled widget AND the agent dashboard
// run on — re-exported behind a stable boundary, plus a small convenience
// client for the common case.
//
//   import { RelayClient } from '@paramms/chat-widget/core'
//
//   // ONE url, any scheme — wss/ws/http(s) all work; ws + REST derived from it.
//   const relay = new RelayClient({ url: 'https://api.relay.paramms.com', token, profileId: 'p_x' })
//   const convo = relay.open({ subjectId: 'listing_42' })            // support thread
//   const dm    = relay.open({ kind: 'direct', peerId: 'user_bob' }) // user↔user (signed identity required)
//   convo.onChange(() => render(convo.store.messages()))
//   convo.send('hello!')
//
// For React, see '@paramms/chat-widget/hooks'.
export { ConnectionManager, type SocketLike } from './connection.js'
export { ChatStore } from './store.js'
export { PersistentOutbox } from './outbox.js'
export { E2ESession } from './e2e.js'
export { restoreHistory, resolveRelayUrls, httpBaseFromWsUrl } from './history.js'
export { mountChatList, type ChatListEntry, type ChatListHandle, type ChatListOptions } from './chatlist.js'
export { persistentUid } from './uid.js'
export * from './protocol/index.js'

import { ConnectionManager } from './connection.js'
import { ChatStore } from './store.js'
import type { ClientFrame, ServerFrame, ConversationId, UserId } from './protocol/index.js'
import { asUserId } from './protocol/index.js'
import { persistentUid } from './uid.js'
import { resolveRelayUrls } from './history.js'

export interface RelayClientOptions {
  /** Relay URL — ONE url, any scheme. `https://api.relay.paramms.com` is the
   *  recommended form; the WebSocket URL (`wss://…/ws`) and REST base are
   *  derived from it automatically. `wss://`/`ws://`/`http://` also accepted. */
  url: string
  /** HTTP(S) base for REST calls — only when the REST API lives on a
   *  DIFFERENT origin than the socket. Normally omit.
   *  @deprecated pass a single `url`; kept for back-compat. */
  apiUrl?: string
  /** Identity: a signed JWT (secure), a stable userId (host-vouched), or omit
   *  for an anonymous per-browser guest (browser environments only). */
  token?: string
  /** Chatroom id (from the dashboard). Required to open conversations. */
  profileId: string
}

export interface OpenOptions {
  /** Support thread scoped to a subject (listing/order/…): one thread per
   *  (user, subject). Omit for the profile's single support thread. */
  subjectId?: string
  subjectTitle?: string
  /** User↔user conversation (requires the chatroom to have signed identity
   *  and `token` to be a valid signed JWT). */
  kind?: 'direct'
  peerId?: string
  /** Display info persisted for agents (support threads only). */
  user?: { name?: string; email?: string; avatar?: string; meta?: Record<string, string> }
}

/** One conversation = one connection + one store. Deliberately thin: the
 *  store is the source of truth, `onChange` is the render signal, everything
 *  else is the same primitives the first-party UIs use. */
export class RelayConversation {
  readonly store: ChatStore
  private readonly conn: ConnectionManager
  private readonly listeners = new Set<() => void>()
  private msgSeq = 0
  private _status = 'connecting'
  private _statusMessage: string | undefined

  constructor(opts: RelayClientOptions & OpenOptions & { me: UserId }) {
    this.store = new ChatStore(opts.me)
    const open: Extract<ClientFrame, { type: 'open' }> = {
      type: 'open',
      profileId: opts.profileId as never,
      ...(opts.subjectId ? { subjectId: opts.subjectId as never } : {}),
      ...(opts.subjectTitle ? { subjectTitle: opts.subjectTitle } : {}),
      ...(opts.kind === 'direct' ? { kind: 'direct' as const, peerId: asUserId(opts.peerId ?? '') } : {}),
      ...(opts.user ? { userInfo: opts.user } : {}),
    }
    // Accept any scheme (https/http/wss/ws) — a plain `https://api.…` URL is
    // resolved to the concrete `wss://…/ws` socket endpoint, exactly like the
    // bundled widget's mount(). Before this, RelayClient required a raw
    // WebSocket URL while the React components took `https://` — one URL now
    // works across the entire SDK.
    const { wsUrl } = resolveRelayUrls(opts.url, opts.apiUrl)
    this.conn = new ConnectionManager({
      url: wsUrl,
      token: opts.token ?? opts.me,
      open,
      getCursor: () => this.store.highestSeq(),
      onFrame: (f: ServerFrame) => {
        // The server tells us our CANONICAL id on auth (a signed JWT's sub,
        // not the raw token) — capture it so `mine` checks work under every
        // identity tier.
        if (f.type === 'authed') this._me = f.userId as UserId
        this.store.apply(f); this.emit()
      },
      onStatusChange: (s, msg) => { this._status = s; this._statusMessage = msg; this.emit() },
    })
    this.conn.connect()
  }

  /** Subscribe to any change (message, typing, status). Returns unsubscribe. */
  onChange(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  private emit(): void { for (const fn of this.listeners) fn() }

  private _me: UserId | undefined
  /** Our canonical user id as resolved by the server (JWT sub / userId / anon id). */
  get me(): UserId | undefined { return this._me }
  get conversationId(): ConversationId | undefined { return this.store.conversationId }
  get status(): string { return this._status }
  get statusMessage(): string | undefined { return this._statusMessage }

  send(text: string): void {
    const clientMsgId = `c_${Date.now().toString(36)}_${++this.msgSeq}`
    const cid = this.store.conversationId
    if (!cid) return
    this.store.addOptimistic(clientMsgId, { kind: 'text', text })
    this.conn.send({ type: 'send', conversationId: cid, clientMsgId, content: { kind: 'text', text } })
    this.emit()
  }

  typing(isTyping: boolean, preview?: string): void {
    const cid = this.store.conversationId
    if (!cid) return
    this.conn.send({ type: 'typing', conversationId: cid, isTyping, ...(preview ? { preview } : {}) })
  }

  markRead(): void {
    const cid = this.store.conversationId
    if (!cid) return
    this.conn.send({ type: 'read', conversationId: cid, seq: this.store.highestSeq() })
  }

  close(): void { this.conn.close(); this.listeners.clear() }
}

export class RelayClient {
  constructor(private readonly opts: RelayClientOptions) {}

  /** The identity this client will act as: the token's subject (resolved
   *  server-side), the raw userId, or a persistent anonymous browser id. */
  me(): UserId {
    return asUserId(this.opts.token ?? persistentUid())
  }

  open(open: OpenOptions = {}): RelayConversation {
    return new RelayConversation({ ...this.opts, ...open, me: this.me() })
  }
}
